import InternalChatAPI from '../../api/internalChat';

const sortRooms = rooms =>
  rooms
    .slice()
    .sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0) || b.id - a.id);

const upsertById = (records, item) => {
  if (!item) return records;
  const index = records.findIndex(
    record => Number(record.id) === Number(item.id)
  );
  if (index === -1) return [...records, item];
  const nextRecords = records.slice();
  nextRecords[index] = { ...nextRecords[index], ...item };
  return nextRecords;
};

export const state = {
  rooms: [],
  messagesByRoomId: {},
  users: [],
  activeRoomId: null,
  searchResults: { rooms: [], messages: [] },
  uiFlags: {
    isFetchingRooms: false,
    isFetchingMessages: false,
    isCreatingRoom: false,
    isSendingMessage: false,
    isSearching: false,
  },
};

export const getters = {
  rooms: $state => sortRooms($state.rooms),
  activeRoom: $state =>
    $state.rooms.find(room => Number(room.id) === Number($state.activeRoomId)),
  messagesForActiveRoom: $state =>
    $state.messagesByRoomId[$state.activeRoomId] || [],
  messagesByRoomId: $state => roomId => $state.messagesByRoomId[roomId] || [],
  users: $state => $state.users,
  searchResults: $state => $state.searchResults,
  unreadTotal: $state =>
    $state.rooms.reduce((sum, room) => sum + Number(room.unread_count || 0), 0),
  uiFlags: $state => $state.uiFlags,
};

export const actions = {
  async fetchRooms({ commit }) {
    commit('setUIFlag', { key: 'isFetchingRooms', value: true });
    try {
      const response = await InternalChatAPI.getRooms();
      commit('setRooms', response.data.data || []);
      return response.data.data || [];
    } finally {
      commit('setUIFlag', { key: 'isFetchingRooms', value: false });
    }
  },

  async fetchUsers({ commit }) {
    const response = await InternalChatAPI.getUsers();
    commit('setUsers', response.data.data || []);
    return response.data.data || [];
  },

  async createRoom({ commit }, payload) {
    commit('setUIFlag', { key: 'isCreatingRoom', value: true });
    try {
      const response = await InternalChatAPI.createRoom(payload);
      commit('upsertRoom', response.data.data);
      return response.data.data;
    } finally {
      commit('setUIFlag', { key: 'isCreatingRoom', value: false });
    }
  },

  async updateRoom({ commit }, { roomId, payload }) {
    const response = await InternalChatAPI.updateRoom(roomId, payload);
    commit('upsertRoom', response.data.data);
    return response.data.data;
  },

  async archiveRoom({ commit }, roomId) {
    await InternalChatAPI.archiveRoom(roomId);
    commit('removeRoom', roomId);
  },

  async fetchMessages({ commit }, { roomId, beforeId, limit }) {
    commit('setUIFlag', { key: 'isFetchingMessages', value: true });
    try {
      const response = await InternalChatAPI.getMessages({
        roomId,
        beforeId,
        limit,
      });
      commit('setMessages', {
        roomId,
        messages: response.data.data || [],
        prepend: !!beforeId,
      });
      return response.data.data || [];
    } finally {
      commit('setUIFlag', { key: 'isFetchingMessages', value: false });
    }
  },

  async sendMessage({ commit }, { roomId, payload }) {
    commit('setUIFlag', { key: 'isSendingMessage', value: true });
    try {
      const response = await InternalChatAPI.createMessage(roomId, payload);
      commit('upsertMessage', response.data.data);
      return response.data.data;
    } finally {
      commit('setUIFlag', { key: 'isSendingMessage', value: false });
    }
  },

  async updateMessage({ commit }, { roomId, messageId, payload }) {
    const response = await InternalChatAPI.updateMessage({
      roomId,
      messageId,
      payload,
    });
    commit('upsertMessage', response.data.data);
    return response.data.data;
  },

  async deleteMessage({ commit }, { roomId, messageId }) {
    await InternalChatAPI.deleteMessage({ roomId, messageId });
    commit('markMessageDeleted', { roomId, messageId });
  },

  async markRead({ commit }, { roomId, messageId }) {
    const response = await InternalChatAPI.markRead({ roomId, messageId });
    commit('upsertRoom', response.data.data);
    return response.data.data;
  },

  async addMembers({ commit }, { roomId, userIds }) {
    const response = await InternalChatAPI.createMembership({
      roomId,
      userIds,
    });
    commit('upsertRoom', response.data.data);
    return response.data.data;
  },

  async updateMembers({ commit }, { roomId, userIds }) {
    const response = await InternalChatAPI.updateMemberships({
      roomId,
      userIds,
    });
    commit('upsertRoom', response.data.data);
    return response.data.data;
  },

  async removeMember({ commit }, { roomId, userId }) {
    const response = await InternalChatAPI.removeMembership({ roomId, userId });
    commit('upsertRoom', response.data.data);
    return response.data.data;
  },

  async search({ commit }, query) {
    if (!query) {
      commit('setSearchResults', { rooms: [], messages: [] });
      return { rooms: [], messages: [] };
    }
    commit('setUIFlag', { key: 'isSearching', value: true });
    try {
      const response = await InternalChatAPI.search(query);
      commit('setSearchResults', response.data.data || {});
      return response.data.data || {};
    } finally {
      commit('setUIFlag', { key: 'isSearching', value: false });
    }
  },

  selectRoom({ commit }, roomId) {
    commit('setActiveRoomId', roomId);
  },

  handleMessageCreated({ commit }, payload) {
    commit('upsertMessage', payload);
    if (payload.room) commit('upsertRoom', payload.room);
  },

  handleMessageUpdated({ commit }, payload) {
    commit('upsertMessage', payload);
  },

  handleMessageDeleted({ commit }, payload) {
    commit('upsertMessage', payload);
  },

  handleRoomUpdated({ commit }, payload) {
    commit('upsertRoom', payload);
  },

  handleMembershipUpdated({ commit }, payload) {
    commit('upsertRoom', payload);
  },

  handleReadUpdated({ commit, rootGetters }, payload) {
    if (Number(payload.user_id) !== Number(rootGetters.getCurrentUserID))
      return;

    commit('patchRoom', {
      id: payload.room_id,
      unread_count: 0,
    });
  },
};

export const mutations = {
  setRooms($state, rooms) {
    $state.rooms = sortRooms(rooms);
  },
  upsertRoom($state, room) {
    $state.rooms = sortRooms(upsertById($state.rooms, room));
  },
  patchRoom($state, patch) {
    $state.rooms = sortRooms(upsertById($state.rooms, patch));
  },
  removeRoom($state, roomId) {
    $state.rooms = $state.rooms.filter(
      room => Number(room.id) !== Number(roomId)
    );
  },
  setUsers($state, users) {
    $state.users = users;
  },
  setActiveRoomId($state, roomId) {
    $state.activeRoomId = roomId ? Number(roomId) : null;
  },
  setMessages($state, { roomId, messages, prepend = false }) {
    const existing = $state.messagesByRoomId[roomId] || [];
    const nextMessages = prepend ? [...messages, ...existing] : messages;
    $state.messagesByRoomId = {
      ...$state.messagesByRoomId,
      [roomId]: nextMessages,
    };
  },
  upsertMessage($state, message) {
    if (!message?.room_id) return;
    const roomId = message.room_id;
    const messages = $state.messagesByRoomId[roomId] || [];
    const nextMessages = upsertById(messages, message).sort(
      (a, b) => a.id - b.id
    );
    $state.messagesByRoomId = {
      ...$state.messagesByRoomId,
      [roomId]: nextMessages,
    };
  },
  markMessageDeleted($state, { roomId, messageId }) {
    const messages = $state.messagesByRoomId[roomId] || [];
    $state.messagesByRoomId = {
      ...$state.messagesByRoomId,
      [roomId]: messages.map(message =>
        Number(message.id) === Number(messageId)
          ? {
              ...message,
              content: null,
              deleted_at: Math.floor(Date.now() / 1000),
            }
          : message
      ),
    };
  },
  setSearchResults($state, results) {
    $state.searchResults = {
      rooms: results.rooms || [],
      messages: results.messages || [],
    };
  },
  setUIFlag($state, { key, value }) {
    $state.uiFlags[key] = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
